import { useEffect, useState } from "react";
import {
  Datagrid,
  List,
  NumberField,
  TextField,
  FunctionField,
  Button,
  Pagination,
  TextInput,
  DateInput,
  SelectInput,
  TopToolbar,
  usePermissions,
} from "react-admin";
import type { RaRecord } from "react-admin";
import { formatListDate } from "@/utils/date-format";
import DownloadIcon from "@mui/icons-material/Download";
import { Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import TallyExportButton from "@/components/TallyExportButton";
import { callApi } from "@/dataprovider/misc-apis";
import { isYearlyNiyaazTallyConfigReady } from "@/types/tally-config";
import { hasPermission } from "@/utils/permission-utils";
import YearlyNiyaazReceiptPaymentSummary from "./YearlyNiyaazReceiptPaymentSummary";

const useHijriYears = () => {
  const [years, setYears] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    callApi({ location: "yearlyNiyaaz", method: "GET", id: "hijri-years" })
      .then((res) => {
        const data = res.data as string[];
        setYears(data.map((y) => ({ id: y, name: y })));
      })
      .catch(() => {});
  }, []);
  return years;
};

const YearlyNiyaazReceiptListActions = () => {
  const { permissions } = usePermissions();
  const canView = hasPermission(permissions, "yearlyNiyaazReceipts.view");

  return (
    <TopToolbar>
      {canView ? (
        <TallyExportButton
          module="yearlyNiyaaz"
          filenamePrefix="yearly-niyaaz-tally"
          isConfigReady={isYearlyNiyaazTallyConfigReady}
        />
      ) : null}
    </TopToolbar>
  );
};

export const YearlyNiyaazReceiptList = () => {
  const years = useHijriYears();

  const filters = [
    <SelectInput
      source="hijriYear"
      label="Hijri Year"
      choices={years}
      alwaysOn
      key="hijriYear"
      sx={{ minWidth: 150 }}
    />,
    <TextInput
      source="q"
      label="Search (receipt no., form no., name, ITS, amount, mode, ref, remarks, creator…)"
      alwaysOn
      key="q"
      sx={{ minWidth: 300 }}
    />,
    <DateInput source="startDate" label="From" alwaysOn key="startDate" />,
    <DateInput source="endDate" label="To" alwaysOn key="endDate" />,
  ];

  return (
    <List
      actions={<YearlyNiyaazReceiptListActions />}
      pagination={<Pagination rowsPerPageOptions={[10, 25, 50]} />}
      sort={{ field: "receiptDate", order: "DESC" }}
      filters={filters}
    >
      <YearlyNiyaazReceiptPaymentSummary />
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="receiptNo" label="Receipt No" />
        <FunctionField
          label="Form No"
          render={(rec: RaRecord) => {
            const yn = rec?.yearlyNiyaaz as { id?: string; formNo?: string } | undefined;
            if (!yn?.id) return <span>-</span>;
            return (
              <Link
                component={RouterLink}
                to={`/yearlyNiyaaz/${yn.id}/show`}
                onClick={(e) => e.stopPropagation()}
              >
                {yn.formNo || yn.id}
              </Link>
            );
          }}
        />
        <FunctionField
          label="Hijri Year"
          render={(rec: RaRecord) =>
            (rec?.yearlyNiyaaz as { hijriYear?: string } | undefined)?.hijriYear || "-"
          }
        />
        <FunctionField
          label="Name"
          render={(rec: RaRecord) =>
            (rec?.yearlyNiyaaz as { name?: string } | undefined)?.name || "-"
          }
        />
        <FunctionField
          label="ITS No"
          render={(rec: RaRecord) =>
            (rec?.yearlyNiyaaz as { itsNo?: string } | undefined)?.itsNo || "-"
          }
        />
        <FunctionField
          label="Date"
          sortBy="receiptDate"
          render={(rec: RaRecord) => formatListDate(rec?.receiptDate, { empty: "—" })}
        />
        <NumberField source="amount" label="Amount" />
        <TextField source="paymentMode" label="Mode" />
        <TextField source="paymentRef" label="Payment Ref" />
        <TextField source="remarks" label="Remarks" />
        <FunctionField
          label="Created By"
          render={(rec: RaRecord) =>
            (rec?.admin as { name?: string } | undefined)?.name || rec.createdBy || "-"
          }
        />
        <FunctionField
          label="Download"
          render={(rec: RaRecord) => (
            <Button
              onClick={() => {
                window.open(`#/yn-rcpt/${rec.id}`, "_blank");
              }}
            >
              <DownloadIcon />
            </Button>
          )}
        />
      </Datagrid>
    </List>
  );
};

export default YearlyNiyaazReceiptList;
